/**
 * 设置迁移工具
 * 用于自动更新旧版本的配置到新版本
 */

export class SettingsMigration {
  /**
   * 执行设置迁移
   */
  static migrateSettings() {
    console.log('🔄 开始设置迁移检查...')
    
    try {
      // 获取当前设置
      const currentSettings = this.getCurrentSettings()
      
      if (!currentSettings) {
        console.log('📝 未找到现有设置，跳过迁移')
        return false
      }
      
      let migrated = false
      const newSettings = { ...currentSettings }
      
      // 迁移1: 更新豆包API端口从3001到3003
      if (this.needsPortMigration(currentSettings)) {
        console.log('🔄 迁移豆包API端口配置...')
        newSettings.imageApiUrl = newSettings.imageApiUrl.replace('localhost:3001', 'localhost:3003')
        migrated = true
        console.log('✅ 端口迁移完成:', newSettings.imageApiUrl)
      }
      
      // 迁移2: 更新豆包模型名称
      if (this.needsModelNameMigration(currentSettings)) {
        console.log('🔄 迁移豆包模型名称...')
        newSettings.imageModel = 'doubao-seedream-4-0-250828'
        migrated = true
        console.log('✅ 模型名称迁移完成:', newSettings.imageModel)
      }
      
      // 保存迁移后的设置
      if (migrated) {
        this.saveSettings(newSettings)
        console.log('✅ 设置迁移完成并已保存')
        
        // 显示迁移通知
        this.showMigrationNotification()
        
        return true
      } else {
        console.log('✅ 设置已是最新版本，无需迁移')
        return false
      }
      
    } catch (error) {
      console.error('❌ 设置迁移失败:', error)
      return false
    }
  }
  
  /**
   * 获取当前设置
   */
  static getCurrentSettings() {
    try {
      const settingsStr = localStorage.getItem('altlife-settings')
      return settingsStr ? JSON.parse(settingsStr) : null
    } catch (error) {
      console.error('❌ 读取设置失败:', error)
      return null
    }
  }
  
  /**
   * 保存设置
   */
  static saveSettings(settings) {
    try {
      localStorage.setItem('altlife-settings', JSON.stringify(settings))
      return true
    } catch (error) {
      console.error('❌ 保存设置失败:', error)
      return false
    }
  }
  
  /**
   * 检查是否需要端口迁移
   */
  static needsPortMigration(settings) {
    return settings.imageApiUrl && 
           settings.imageApiUrl.includes('localhost:3001') &&
           settings.imageApiUrl.includes('doubao')
  }
  
  /**
   * 检查是否需要模型名称迁移
   */
  static needsModelNameMigration(settings) {
    return settings.imageModel === 'doubao-seedream-4-0' ||
           (settings.imageModel && settings.imageModel.includes('doubao') && 
            !settings.imageModel.includes('250828'))
  }
  
  /**
   * 显示迁移通知
   */
  static showMigrationNotification() {
    // 创建通知元素
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #4f46e5, #7c3aed);
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      max-width: 400px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.5;
    `
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <span style="font-size: 18px; margin-right: 8px;">🔄</span>
        <strong>设置已自动更新</strong>
      </div>
      <div style="margin-bottom: 12px;">
        豆包API配置已更新到新的端口 (3003)，图片生成功能已恢复正常。
      </div>
      <button onclick="this.parentElement.remove()" style="
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: white;
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
      ">知道了</button>
    `
    
    document.body.appendChild(notification)
    
    // 5秒后自动消失
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove()
      }
    }, 5000)
  }
  
  /**
   * 检查设置版本
   */
  static getSettingsVersion() {
    const settings = this.getCurrentSettings()
    return settings?.version || '1.0.0'
  }
  
  /**
   * 设置当前版本
   */
  static setSettingsVersion(version = '1.1.0') {
    const settings = this.getCurrentSettings() || {}
    settings.version = version
    this.saveSettings(settings)
  }
  
  /**
   * 重置所有设置（危险操作）
   */
  static resetAllSettings() {
    if (confirm('⚠️ 确定要重置所有设置吗？这将清除所有自定义配置。')) {
      localStorage.removeItem('altlife-settings')
      console.log('🔄 所有设置已重置')
      location.reload()
    }
  }
  
  /**
   * 导出设置
   */
  static exportSettings() {
    const settings = this.getCurrentSettings()
    if (!settings) {
      alert('❌ 没有找到可导出的设置')
      return
    }
    
    const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    
    const a = document.createElement('a')
    a.href = url
    a.download = `altlife-settings-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    console.log('✅ 设置已导出')
  }
  
  /**
   * 导入设置
   */
  static importSettings(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        try {
          const settings = JSON.parse(e.target.result)
          this.saveSettings(settings)
          console.log('✅ 设置已导入')
          resolve(settings)
        } catch (error) {
          console.error('❌ 设置导入失败:', error)
          reject(error)
        }
      }
      
      reader.onerror = () => {
        reject(new Error('文件读取失败'))
      }
      
      reader.readAsText(file)
    })
  }
}

export default SettingsMigration
